<template>
  <div class="container-fluid p-0">
    <div class="row g-3">
      <!-- File Upload Card -->
      <div class="col-lg-6 col-md-6">
        <AiChatCard
          :companyId="props.companyId"
          :endpoint="`/api/company/${props.companyId}/portfolio/send-chat`"
          :greeting="$t('ai_chat.greetings.send')"
          @analysis-complete="handleFileAnalysisComplete"
        />
      </div>

      <!-- Order summary -->
      <div class="col-lg-6 col-md-6">
        <div class="card h-100">
          <div class="card-header bg-light py-2">
            <h6 class="card-title mb-0 fw-semibold">
              {{ t("dashboard.portfolio.sendGoOrderSummary") }}
            </h6>
          </div>
          <div class="card-body">
            <form
              @submit.prevent="openReviewModal"
              class="d-flex flex-column h-100"
            >
              <SendRecipientForm
                v-model="sendingOrder.recipient"
                v-model:aiCompanyName="aiCompanyName"
                :authority="selectionAuthority"
                :showInternalAccount="!isTgeSelection"
                :disabled="isConfirmingOrder"
              >
                <template #external-fields>
                  <div v-if="requiresUnitPrice" class="mb-3">
                    <label for="unitPricePln" class="form-label small">{{
                      t("go_send.unit_price_pln_label")
                    }}</label>
                    <input
                      type="text"
                      inputmode="decimal"
                      class="form-control form-control-sm"
                      :class="{ 'is-invalid': showUnitPriceValidationError }"
                      id="unitPricePln"
                      v-model="sendingOrder.unitPricePln"
                      :disabled="isConfirmingOrder"
                    />
                    <div
                      v-if="showUnitPriceValidationError"
                      class="form-text text-danger"
                    >
                      {{ t("validations.unit_price_pln_format") }}
                    </div>
                    <div class="form-text text-muted">
                      {{ t("go_send.unit_price_pln_help") }}
                    </div>
                  </div>
                </template>
              </SendRecipientForm>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasMixedAuthoritySelection" class="row g-3 mt-3">
      <div class="col-12">
        <div class="alert alert-danger">
          <FontAwesomeIcon icon="exclamation-triangle" class="me-2" />
          {{ t("go_send.mixed_registry_selection_error") }}
        </div>
      </div>
    </div>

    <!-- Insufficient Inventory Warning -->
    <div v-if="showInsufficientInventoryWarning" class="row g-3 mt-3">
      <div class="col-12">
        <div class="alert alert-warning">
          <FontAwesomeIcon icon="exclamation-triangle" class="me-2" />
          {{
            t("go_send.insufficient_inventory_warning", {
              availableMwh: totalAvailableMwhInInventory,
              neededMwh: totalMwhRequestedFromFile ?? totalSelectedMwh,
            })
          }}
        </div>
      </div>
    </div>

    <!-- Add the PortfolioItemsTable below the chat/summary row -->
    <div class="row g-3 mt-3">
      <div class="col-12">
        <h5 class="mb-3">
          {{ t("dashboard.portfolio.selected_certificates") }}
        </h5>
        <PortfolioItemsTable
          ref="portfolioTableRef"
          :selection="selection"
          :enable-count-editing="true"
          :default-visible-columns="TRANSACTION_COLUMNS"
          storage-context="send"
          @update:filtered-stats="onFilteredStats"
        >
          <template #footer-action>
            <button
              type="button"
              class="btn btn-success"
              @click="openReviewModal"
              :disabled="!isOrderComplete || isConfirmingOrder"
            >
              <span
                v-if="isConfirmingOrder"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t("common.actions.confirm_send") }}
            </button>
          </template>
        </PortfolioItemsTable>
      </div>
    </div>

    <SendGoReviewModal
      ref="reviewModalRef"
      :recipientCompanyName="sendingOrder.recipient.companyName"
      :recipientCountryDisplay="recipientCountryDisplay"
      :recipientEecsAccountNumber="sendingOrder.recipient.eecsAccountNumber"
      :recipientRegistryInternalAccountNumber="
        sendingOrder.recipient.internalAccountNumber
      "
      :unitPricePln="submittableUnitPricePln"
      :isHostedRecipient="isHostedRecipient"
      :totalMwh="totalSelectedMwh"
      :technologies="selectedTechnologies"
      :productionMonths="selectedProductionMonths"
      :productionSupport="summarySupport"
      :isLoading="isConfirmingOrder"
      :confirmDisabled="!isOrderComplete"
      @confirm="handleConfirmSendOrder"
    />
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  type CertificateSelectionCriteria,
  resolveCertificateSelectionFilters,
} from "@/dashboard/inventory_management/common/cancelAiMapping";
import { isItemAvailable } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import {
  type SelectableFilterColumn,
  TRANSACTION_COLUMNS,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { useSelectedPortfolioSummary } from "@/dashboard/inventory_management/common/useSelectedPortfolioSummary";
import SendGoReviewModal from "@/dashboard/inventory_management/send/SendGoReviewModal.vue";
import {
  EMPTY_SEND_RECIPIENT,
  isHostedRecipient as isHosted,
  isRecipientComplete,
  recipientCountryDisplay as countryDisplayFor,
  type RecipientFields,
  type SendRecipient,
  toRecipientFields,
} from "@/dashboard/inventory_management/send/sendRecipient";
import SendRecipientForm from "@/dashboard/inventory_management/send/SendRecipientForm.vue";
import {
  type FilteredPortfolioStats,
  type FlattenedPortfolioItem,
  PortfolioItemTransfer,
} from "@/types";
import {
  type RegistrationAuthority,
  registrationAuthorityForRegion,
} from "@/utils/registryAccounts";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import AiChatCard from "../common/AiChatCard.vue";

interface SendingOrder {
  recipient: SendRecipient;
  unitPricePln: string;
}

const props = defineProps<{
  companyId: string | number;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler, globalWarningHandler } =
  useGlobalHandlers();
const portfolioItemsStore = usePortfolioItemsStore();
const selection = usePortfolioSelection({ syncToUrl: true });
const {
  selectedPortfolioItems,
  totalSelectedMwh,
  selectedTechnologies,
  selectedProductionMonths,
  summarySupport,
} = useSelectedPortfolioSummary(selection);

const isConfirmingOrder = ref(false);
const aiCompanyName = ref<string>();
const totalMwhRequestedFromFile = ref<number | null>(null);
const reviewModalRef = ref<{ open: () => void; close: () => void } | null>(
  null
);
const portfolioTableRef = ref<InstanceType<typeof PortfolioItemsTable> | null>(
  null
);

// Items currently visible after table filters, delivered reactively via `@update:filtered-stats`.
const filteredItems = ref<FlattenedPortfolioItem[]>([]);

const onFilteredStats = (stats: FilteredPortfolioStats) => {
  filteredItems.value = stats.items;
};

const UNIT_PRICE_PATTERN = /^\d+([.,]\d{1,2})?$/;

const createSendingOrder = (): SendingOrder => ({
  recipient: { ...EMPTY_SEND_RECIPIENT },
  unitPricePln: "",
});

const sendingOrder = ref<SendingOrder>(createSendingOrder());

const totalAvailableMwhInInventory = computed(() => {
  return portfolioItemsStore.availableItems.reduce((total, item) => {
    const mwh = Number(item.count);
    return total + (isNaN(mwh) || mwh < 0 ? 0 : Math.floor(mwh));
  }, 0);
});

const showInsufficientInventoryWarning = computed(() => {
  const mwhToCheck = totalMwhRequestedFromFile.value ?? totalSelectedMwh.value;
  return mwhToCheck > 0 && totalAvailableMwhInInventory.value < mwhToCheck;
});

const isHostedRecipient = computed(() =>
  isHosted(sendingOrder.value.recipient)
);

const selectedAuthorities = computed(
  () =>
    new Set(
      selectedPortfolioItems.value.map((item) =>
        registrationAuthorityForRegion(item.registry_region)
      )
    )
);

// NOTE: a transfer always executes inside one authority's registry — there is no
// inter-authority transfer — so a selection spanning two of them cannot be sent at all.
const hasMixedAuthoritySelection = computed(
  () => selectedAuthorities.value.size > 1
);

const selectionAuthority = computed<RegistrationAuthority>(() =>
  selectedAuthorities.value.size === 1
    ? [...selectedAuthorities.value][0]
    : "AIB"
);

// NOTE: TGE is an island — it only moves certificates PL to PL, so an RGP selection always
// goes to a TGE member code.
const isTgeSelection = computed(() => selectionAuthority.value === "RGP");

// NOTE: hosted sends route to Soldera's own TGE account, where the transfer records a
// placeholder price, so only an external TGE recipient needs a user-entered unit price.
const requiresUnitPrice = computed(
  () => isTgeSelection.value && !isHostedRecipient.value
);

const recipientCountryDisplay = computed(() =>
  countryDisplayFor(sendingOrder.value.recipient, selectionAuthority.value)
);

const isUnitPriceValid = computed(() => {
  const raw = sendingOrder.value.unitPricePln.trim();
  return UNIT_PRICE_PATTERN.test(raw) && Number(raw.replace(",", ".")) > 0;
});

const showUnitPriceValidationError = computed(
  () => !!sendingOrder.value.unitPricePln.trim() && !isUnitPriceValid.value
);

const submittableUnitPricePln = computed(() =>
  requiresUnitPrice.value && isUnitPriceValid.value
    ? Number(sendingOrder.value.unitPricePln.trim().replace(",", ".")).toFixed(
        2
      )
    : undefined
);

const isOrderComplete = computed(() => {
  return (
    isRecipientComplete(
      sendingOrder.value.recipient,
      selectionAuthority.value
    ) &&
    !hasMixedAuthoritySelection.value &&
    (!requiresUnitPrice.value || isUnitPriceValid.value) &&
    selectedPortfolioItems.value.length > 0 &&
    totalSelectedMwh.value > 0 &&
    !isConfirmingOrder.value
  );
});

const openReviewModal = () => {
  if (isOrderComplete.value) {
    reviewModalRef.value?.open();
  }
};

const handleConfirmSendOrder = async () => {
  if (!isOrderComplete.value || isConfirmingOrder.value) return;
  isConfirmingOrder.value = true;
  try {
    const orderPayload: RecipientFields & {
      portfolio_items: PortfolioItemTransfer[];
      unit_price_pln?: string;
    } = {
      ...toRecipientFields(sendingOrder.value.recipient),
      portfolio_items: selection.selectedItemsForApi,
    };
    if (submittableUnitPricePln.value) {
      orderPayload.unit_price_pln = submittableUnitPricePln.value;
    }
    const currentCompanyId = Number(props.companyId);
    if (isNaN(currentCompanyId)) {
      globalErrorHandler(t("errors.missingCompanyId"));
      isConfirmingOrder.value = false;
      return;
    }
    await goSolidApi.post(
      `/api/company/${currentCompanyId}/portfolio/send`,
      orderPayload
    );

    // Clear local state
    selection.clearSelection();
    totalMwhRequestedFromFile.value = null;
    sendingOrder.value = createSendingOrder();
    aiCompanyName.value = undefined;

    // Refetch portfolio items from server
    await portfolioItemsStore.refetchItems();

    globalSuccessHandler(
      t("common.success"),
      t("dashboard.portfolio.sendGoSuccessMessage")
    );
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    isConfirmingOrder.value = false;
  }
};

const handleFileAnalysisComplete = async (data: any) => {
  if (!data) {
    return;
  }

  try {
    const formData = data.form_data;

    totalMwhRequestedFromFile.value = null;

    if (formData.recipient_company_name) {
      aiCompanyName.value = formData.recipient_company_name;
    }
    sendingOrder.value.recipient = recipientFromDocument(
      sendingOrder.value.recipient,
      formData
    );

    const criteria = mapSendCriteriaToSelection(formData?.selection_criteria);
    const totalMwh =
      typeof data.total_mwh_requested === "number"
        ? data.total_mwh_requested
        : null;

    if (criteria || totalMwh) {
      // Apply the AI's document criteria as table filters (leaving any manual filters on other
      // columns intact), then select oldest-first from the resulting filtered set — so the picks
      // honor both the AI's criteria AND the operator's active filters, matching the cancel tab.
      const selectedMwh = await applyCriteriaAndSelect(criteria, totalMwh);
      if (selectedMwh <= 0) {
        // The criteria matched no available certificates (unavailable technology/device/period, or a
        // stale manual filter), so the table is empty and nothing was selected. Don't claim success —
        // tell the operator to review the criteria instead of leaving a confusing "all set" toast.
        globalWarningHandler(
          t("dashboard.portfolio.fileUpload.analysisNoMatchTitle"),
          t("dashboard.portfolio.fileUpload.analysisNoMatchMessage")
        );
      } else if (totalMwh && selectedMwh < totalMwh) {
        // Some stock matched, but the filtered inventory can't cover the requested volume — the
        // transfer would be under-filled. Warn instead of reporting success so the operator reviews
        // the shortfall before confirming (the persistent insufficient-inventory banner also shows).
        globalWarningHandler(
          t("dashboard.portfolio.fileUpload.analysisShortfallTitle"),
          t("dashboard.portfolio.fileUpload.analysisShortfallMessage", {
            selected: selectedMwh,
            requested: totalMwh,
          })
        );
      } else {
        globalSuccessHandler(
          t("dashboard.portfolio.fileUpload.analysisSuccessTitle"),
          t("dashboard.portfolio.fileUpload.analysisSuccessMessage")
        );
      }
    } else {
      // A completed analysis with no selection criteria and no requested volume must still clear any
      // selection and AI-controlled filters left over from a previous document — otherwise
      // usePortfolioSelection carries the prior picks into selectedItemsForApi and could submit
      // certificates from an earlier upload.
      resetAiControlledFilters();
      selection.clearSelection();
    }

    if (totalMwh) {
      totalMwhRequestedFromFile.value = totalMwh;
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

// A document names an external recipient, never a hosted account, so any recipient detail it
// supplies replaces a hosted pick. Fields the document leaves out keep their current values.
const recipientFromDocument = (
  current: SendRecipient,
  formData: any
): SendRecipient => {
  const internalAccount = formData.recipient_registry_internal_account_number;
  const recipient: SendRecipient = {
    ...current,
    companyName: formData.recipient_company_name || current.companyName,
    eecsAccountNumber:
      formData.recipient_eecs_account_number || current.eecsAccountNumber,
    internalAccountNumber: Object.prototype.hasOwnProperty.call(
      formData,
      "recipient_registry_internal_account_number"
    )
      ? typeof internalAccount === "string"
        ? internalAccount.trim()
        : ""
      : current.internalAccountNumber,
  };
  const suppliesRecipient =
    !!formData.recipient_company_name ||
    !!formData.recipient_eecs_account_number ||
    !!recipient.internalAccountNumber;
  return suppliesRecipient ? { ...recipient, companyId: null } : recipient;
};

// The table columns that resolveCertificateSelectionFilters can drive from document criteria. Reset
// these between uploads so one document's criteria never linger as hidden filters on the next.
const AI_CONTROLLED_FILTER_COLUMNS: SelectableFilterColumn[] = [
  "technology_type",
  "production_month",
  "issuing_region",
  "production_device_name",
  "portfolio_name",
];

// Clears every AI-controllable column filter, so a previous upload's criteria never linger. Manual
// filters on other columns are left intact. No-op if the table ref isn't mounted yet.
const resetAiControlledFilters = () => {
  portfolioTableRef.value?.clearFilters(AI_CONTROLLED_FILTER_COLUMNS);
};

// Send's extraction criteria (ISO YYYY-MM-DD periods) reshaped into the shared resolver's
// CertificateSelectionCriteria (YYYY-MM vintage months). Only the columns send extracts are set.
//
// Fail-closed on unparseable production bounds: when a period bound is PRESENT in the raw payload
// but does NOT parse to YYYY-MM, return a criteria object with the unparseable bound so the shared
// resolver `resolveCertificateSelectionFilters` applies its unmatchable-sentinel path (which yields
// an empty table and fires the selectedCount===0 warning, matching the cancel tab's behavior).
const mapSendCriteriaToSelection = (
  raw: any
): CertificateSelectionCriteria | null => {
  if (!raw) {
    return null;
  }
  // Returns the parsed YYYY-MM vintage month if parseable, or the SENTINEL for present-but-unparseable.
  const UNPARSEABLE_SENTINEL = "UNPARSEABLE";
  const toVintageMonth = (value: unknown, fieldName: string): string | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const match = /^(\d{4})-(\d{2})/.exec(trimmed);
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
    // Present but unparseable: return a sentinel that resolveCertificateSelectionFilters will fail-close on.
    console.warn(
      `[SendGoComponent] ${fieldName} is present but unparseable: "${trimmed}". Failing closed.`
    );
    return UNPARSEABLE_SENTINEL;
  };
  const cleanList = (value: unknown): string[] | null => {
    if (!Array.isArray(value)) return null;
    const cleaned = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter((entry): entry is string => !!entry);
    return cleaned.length ? cleaned : null;
  };
  const criteria: CertificateSelectionCriteria = {
    technologyTypes: cleanList(raw.technology_types),
    productionPeriodStart: toVintageMonth(
      raw.production_period_start,
      "production_period_start"
    ),
    productionPeriodEnd: toVintageMonth(
      raw.production_period_end,
      "production_period_end"
    ),
    issuingRegions: null,
    registryRegions: null,
    codAge: null,
    productionSupport: null,
    ecolabels: null,
    productionDeviceNames: cleanList(raw.production_device_names),
    portfolioNames: null,
  };
  return Object.values(criteria).some((value) => value !== null)
    ? criteria
    : null;
};

// Selects oldest-vintage-first from the reactively filtered set, so document criteria (applied as
// table filters) and any manual operator filters are both honored. Mirrors the cancel tab.
// Returns the total MWh selected, so callers can tell whether the criteria matched anything.
const selectFromFilteredForMwh = (mwhNeeded: number | null): number => {
  selection.clearSelection();

  const availableItems = filteredItems.value.filter(isItemAvailable);
  const sortedItems = [...availableItems].sort((a, b) => {
    const monthA = a.production_month ?? "9999-99";
    const monthB = b.production_month ?? "9999-99";
    if (monthA !== monthB) {
      return monthA.localeCompare(monthB);
    }
    return a.count - b.count;
  });

  // No MWh target: select the whole filtered set (mirrors the backend's filters-only strategy).
  if (mwhNeeded === null || mwhNeeded <= 0) {
    let selected = 0;
    sortedItems.forEach((item) => {
      selection.updateItemSelectedCount(item.id, item.count);
      selected += item.count;
    });
    return selected;
  }

  let remaining = mwhNeeded;
  for (const item of sortedItems) {
    if (remaining <= 0) break;
    const selectCount = Math.min(item.count, remaining);
    selection.updateItemSelectedCount(item.id, selectCount);
    remaining -= selectCount;
  }
  return mwhNeeded - Math.max(remaining, 0);
};

// Returns the total MWh actually selected (0 when the criteria matched no available certificates).
const applyCriteriaAndSelect = async (
  criteria: CertificateSelectionCriteria | null,
  totalMwh: number | null
): Promise<number> => {
  if (criteria && portfolioTableRef.value) {
    const resolved = resolveCertificateSelectionFilters(
      criteria,
      portfolioTableRef.value.getFilterOptions()
    );
    // Reset every AI-controllable column first (a device filter from an earlier document would
    // otherwise silently empty this one's table), then apply this document's resolved set — leaving
    // the operator's manual filters on other columns intact.
    portfolioTableRef.value.clearFilters(AI_CONTROLLED_FILTER_COLUMNS);
    portfolioTableRef.value.applyColumnFilters(resolved.filters);
    // Let the table re-run filtering so `filteredItems` reflects the new criteria before selecting.
    // The table emits update:filtered-stats from a post-render effect, so one flush is enough.
    await nextTick();
  }
  return selectFromFilteredForMwh(totalMwh);
};

watch(requiresUnitPrice, (required) => {
  if (!required) {
    sendingOrder.value.unitPricePln = "";
  }
});

onUnmounted(() => {
  selection.clearSelection();
});
</script>

<style scoped lang="scss"></style>
